/* ================================================================
   pendleton-search-bar.js
   Pendleton Japan – ヘッダー検索バー（本番用）
   - Cloudflare KV: 人気ワード + 個人履歴
   - Shopify Storefront API: 商品サジェスト
   ================================================================ */
(function () {
  'use strict';

  /* ── 設定（window.__psdCfg はsnippetのinline scriptで注入） ── */
  function cfg(key) {
    return ((window.__psdCfg || {})[key]) || '';
  }
  const DEBOUNCE_MS   = 180;
  const SUGGEST_LIMIT = 6;
  const POPULAR_SHOW  = 10;
  const HISTORY_SHOW  = 8;
  const KEY_UID       = 'psd_uid_v1';
  const KEY_HIST      = 'psd_search_hist';

  /* ── UID ── */
  function getOrCreateUid() {
    try {
      let uid = localStorage.getItem(KEY_UID);
      if (!uid) {
        uid = 'u-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(KEY_UID, uid);
      }
      return uid;
    } catch { return 'anonymous'; }
  }

  /* ── ローカル履歴 ── */
  function getLocalHistory() {
    try { return JSON.parse(localStorage.getItem(KEY_HIST) || '[]'); } catch { return []; }
  }
  function addLocalHistory(word) {
    let hist = getLocalHistory().filter(w => w !== word);
    hist.unshift(word);
    try { localStorage.setItem(KEY_HIST, JSON.stringify(hist.slice(0, 20))); } catch {}
  }

  /* ── エスケープ ── */
  const ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(s) { return String(s).replace(/[&<>"']/g, c => ESC_MAP[c]); }

  /* ── SVG ── */
  const SVG = {
    search: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>',
    clock:  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
    trend:  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
  };

  /* ── クラウド状態（ページ内共有） ── */
  let cloudPopular = [];
  let cloudHistory = [];
  let cloudLoaded  = false;

  async function fetchCloudData() {
    if (cloudLoaded || !cfg('workerUrl')) return;
    const uid = getOrCreateUid();
    try {
      const [popRes, histRes] = await Promise.all([
        fetch(`${cfg('workerUrl')}/popular?limit=${POPULAR_SHOW}`),
        fetch(`${cfg('workerUrl')}/history?uid=${encodeURIComponent(uid)}&limit=${HISTORY_SHOW}`),
      ]);
      if (popRes.ok)  { const d = await popRes.json();  cloudPopular = d.popular  || []; }
      if (histRes.ok) { const d = await histRes.json(); cloudHistory = d.history || []; }
      cloudLoaded = true;
    } catch {}
  }

  function recordSearch(word) {
    addLocalHistory(word);
    if (!cfg('workerUrl')) return;
    const uid = getOrCreateUid();
    // 楽観的更新
    cloudHistory = [word, ...cloudHistory.filter(w => w !== word)].slice(0, HISTORY_SHOW);
    const idx = cloudPopular.findIndex(e => e.query === word);
    if (idx >= 0) { cloudPopular[idx].count++; }
    else { cloudPopular.push({ query: word, count: 1 }); }
    cloudPopular.sort((a, b) => b.count - a.count);
    cloudPopular = cloudPopular.slice(0, POPULAR_SHOW);
    // keepalive: true でページ遷移中もリクエストを完了させる
    fetch(`${cfg('workerUrl')}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: word, uid }),
      keepalive: true,
    }).catch(() => {});
  }

  /* ── Storefront GraphQL API ── */
  async function fetchSuggestions(query, signal) {
    if (!cfg('sfToken')) return null;
    const gql = `{
      search(query: ${JSON.stringify(query)}, first: ${SUGGEST_LIMIT}, types: PRODUCT) {
        edges {
          node {
            ... on Product {
              title
              handle
              featuredImage { url(transform: {maxWidth: 120}) }
              priceRange { minVariantPrice { amount currencyCode } }
            }
          }
        }
      }
    }`;
    try {
      const res = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg('sfToken'),
        },
        body: JSON.stringify({ query: gql }),
        signal,
      });
      if (!res.ok) return null;
      const json = await res.json();
      if (json.errors) return null;
      const edges = json.data?.search?.edges || [];
      return {
        products: edges.map(({ node }) => ({
          title          : node.title,
          url            : `/products/${node.handle}`,
          featured_image : node.featuredImage || null,
          price          : Math.round(parseFloat(node.priceRange.minVariantPrice.amount) * 100),
        })),
      };
    } catch (e) {
      if (e?.name !== 'AbortError') console.warn('[PSB] fetch error:', e);
      return null;
    }
  }

  /* ── デバウンス ── */
  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  /* ── 各 .psb-wrap を初期化 ── */
  function initBar(wrap) {
    const input    = wrap.querySelector('.psb-input');
    const dd       = wrap.querySelector('.psb-dd');
    const clearBtn = wrap.querySelector('.psb-clear');
    const form     = wrap.querySelector('.psb-form');
    if (!input || !dd) return;

    const SEARCH_URL = cfg('searchUrl') || '/search';
    let currentQuery = '';
    let fetchAbort   = null;
    let isComposing  = false;
    let activeIdx    = -1;

    /* ── DD 開閉 ── */
    function openDD() {
      dd.removeAttribute('hidden');
      input.setAttribute('aria-expanded', 'true');
      // SP: ドロップダウンをヘッダー直下に動的配置（キーボードに隠れない）
      if (window.innerWidth <= 767) {
        const hdr = document.querySelector('.header-wrapper') || document.querySelector('header');
        if (hdr) dd.style.top = hdr.getBoundingClientRect().bottom + 'px';
      }
    }
    function closeDD() {
      dd.setAttribute('hidden', '');
      input.setAttribute('aria-expanded', 'false');
      activeIdx = -1;
      dd.style.top = '';
    }

    /* ── キーボードナビ用 選択可能アイテム ── */
    function getNavItems() {
      return [...dd.querySelectorAll('a.psb-dd-item, [data-action="pick"]')];
    }
    function setActive(idx) {
      const items = getNavItems();
      items.forEach((el, i) => el.classList.toggle('is-active', i === idx));
      activeIdx = idx;
      if (items[idx] && items[idx].dataset.word) input.value = items[idx].dataset.word;
    }

    /* ── クリアボタン表示 ── */
    function updateClear() {
      if (clearBtn) clearBtn.hidden = !input.value;
    }

    /* ── 履歴パネル ── */
    function renderHistory() {
      const hist  = cloudHistory.slice(0, HISTORY_SHOW);
      const local = getLocalHistory().slice(0, 5);
      let html = '';

      // ① 検索履歴（先に表示）
      if (hist.length) {
        html += `<div class="psb-dd-label">${SVG.clock}&nbsp;最近の検索</div>`;
        hist.forEach(w => {
          html += `<div class="psb-dd-item" data-word="${esc(w)}" data-action="pick">
            <span class="psb-di">${SVG.clock}</span>
            <span class="psb-dt">${esc(w)}</span>
          </div>`;
        });
      } else if (local.length) {
        html += `<div class="psb-dd-label">${SVG.search}&nbsp;入力履歴</div>`;
        local.forEach(w => {
          html += `<div class="psb-dd-item" data-word="${esc(w)}" data-action="pick">
            <span class="psb-di">${SVG.search}</span>
            <span class="psb-dt">${esc(w)}</span>
          </div>`;
        });
      }

      // ② 人気キーワード（ランクなし）
      if (cloudPopular.length) {
        html += `<div class="psb-dd-label">${SVG.trend}&nbsp;人気キーワード</div><div class="psb-chips">`;
        cloudPopular.slice(0, POPULAR_SHOW).forEach(({ query }) => {
          html += `<a class="psb-chip" href="${esc(SEARCH_URL)}?q=${encodeURIComponent(query)}&type=product">${esc(query)}</a>`;
        });
        html += '</div>';
      }

      if (!html) {
        html = '<div class="psb-dd-empty">検索履歴がありません</div>';
      }

      dd.innerHTML = html;
      openDD();
    }

    /* ── ローディング ── */
    function renderLoading() {
      dd.innerHTML = '<div class="psb-dd-loading"><span class="psb-spinner"></span>検索中...</div>';
      openDD();
    }

    /* ── 結果パネル ── */
    function renderResults(query, results) {
      const products = results?.products || [];
      const qLow     = query.toLowerCase();
      const seen     = new Set();
      const suggs    = [];
      let html = '';

      // ① クラウド履歴マッチ
      cloudHistory.filter(q => q.toLowerCase().includes(qLow)).slice(0, 3).forEach(q => {
        if (!seen.has(q.toLowerCase())) { seen.add(q.toLowerCase()); suggs.push({ text: q, icon: SVG.clock }); }
      });
      // ② 人気ワードマッチ
      cloudPopular.filter(e => e.query.toLowerCase().includes(qLow)).slice(0, 3).forEach(({ query: q }) => {
        if (!seen.has(q.toLowerCase())) { seen.add(q.toLowerCase()); suggs.push({ text: q, icon: SVG.trend }); }
      });
      // ③ ローカル履歴マッチ
      getLocalHistory().filter(q => q.toLowerCase().includes(qLow)).slice(0, 2).forEach(q => {
        if (!seen.has(q.toLowerCase())) { seen.add(q.toLowerCase()); suggs.push({ text: q, icon: SVG.search }); }
      });

      if (suggs.length) {
        html += `<div class="psb-dd-label">${SVG.search}&nbsp;キーワード候補</div>`;
        suggs.slice(0, 6).forEach(({ text, icon }) => {
          const url  = `${SEARCH_URL}?q=${encodeURIComponent(text)}&type=product`;
          const tLow = text.toLowerCase();
          const idx  = tLow.indexOf(qLow);
          let display;
          if (idx >= 0) {
            const before  = esc(text.slice(0, idx));
            const matched = esc(text.slice(idx, idx + query.length));
            const after   = esc(text.slice(idx + query.length));
            display = `${before}<span class="psb-typed">${matched}</span><span class="psb-suggest">${after}</span>`;
          } else {
            display = esc(text);
          }
          html += `<a class="psb-dd-item" href="${esc(url)}">
            <span class="psb-di">${icon}</span>
            <span class="psb-dt">${display}</span>
          </a>`;
        });
      }

      if (products.length) {
        html += '<div class="psb-dd-label">商品</div>';
        products.forEach(({ title, url, featured_image, price }) => {
          const imgSrc   = featured_image?.url ?? '';
          const priceStr = price ? `¥${Number(price / 100).toLocaleString('ja-JP')}` : '';
          html += `<a class="psb-dd-product" href="${esc(url)}">
            ${imgSrc
              ? `<img src="${esc(imgSrc)}" alt="${esc(title)}" loading="lazy">`
              : '<div style="width:44px;height:44px;background:#f0f0f0;border-radius:2px;flex-shrink:0"></div>'}
            <div class="psb-dd-product-info">
              <div class="psb-dd-product-title">${esc(title)}</div>
              ${priceStr ? `<div class="psb-dd-product-price">${priceStr}</div>` : ''}
            </div>
          </a>`;
        });
      }

      if (!suggs.length && !products.length) {
        html = `<div class="psb-dd-empty">「${esc(query)}」に一致する候補がありません</div>`;
      } else {
        html += `<a class="psb-dd-more" href="${esc(SEARCH_URL)}?q=${encodeURIComponent(query)}&type=product">「${esc(query)}」の検索結果をすべて見る →</a>`;
      }

      dd.innerHTML = html;
      openDD();
    }

    /* ── 入力 → サジェスト取得 ── */
    const onInput = debounce(async () => {
      const q = input.value.trim();
      if (isComposing) return;
      currentQuery = q;

      if (!q) { renderHistory(); return; }
      renderLoading();

      if (fetchAbort) fetchAbort.abort();
      fetchAbort = new AbortController();
      const results = await fetchSuggestions(q, fetchAbort.signal);
      if (currentQuery !== q) return;
      renderResults(q, results);
    }, DEBOUNCE_MS);

    /* ── キーボードナビ ── */
    input.addEventListener('keydown', e => {
      if (dd.hasAttribute('hidden')) return;
      const items = getNavItems();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(Math.min(activeIdx + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (activeIdx <= 0) { activeIdx = -1; input.value = currentQuery; items.forEach(el => el.classList.remove('is-active')); }
        else { setActive(activeIdx - 1); }
      } else if (e.key === 'Escape') {
        closeDD();
        input.blur();
      }
    });

    /* ── イベント ── */
    input.addEventListener('compositionstart', () => { isComposing = true; });
    input.addEventListener('compositionend',   () => { isComposing = false; onInput(); });

    // フォーカス前にすでにフォーカス済みかを記録（トグル判定用）
    let alreadyFocused = false;

    // PC: mousedown でフォーカス済みか記録
    input.addEventListener('mousedown', () => {
      alreadyFocused = document.activeElement === input;
    });

    // SP: touchstart でトグル判定 + ペーストメニューを根本から防ぐ
    // passive: false にして preventDefault() を使えるようにする
    input.addEventListener('touchstart', (e) => {
      alreadyFocused = document.activeElement === input;
      if (alreadyFocused && !dd.hasAttribute('hidden')) {
        // DD が開いている状態でタップ → タッチジェスチャーごとキャンセル
        e.preventDefault(); // ペーストメニュー・後続 click をすべて抑制
        closeDD();
      }
    }, { passive: false });

    // 新規フォーカス時 → 開く
    input.addEventListener('focus', async () => {
      if (!alreadyFocused) {
        if (!input.value.trim()) {
          renderHistory(); // まずローカルデータで即表示
          await fetchCloudData(); // クラウドデータ取得を待つ
          // 取得完了後、まだ入力なし＆DD表示中なら再描画
          if (!input.value.trim() && !dd.hasAttribute('hidden')) renderHistory();
        } else {
          openDD();
        }
      }
    });
    // PC: フォーカス済みの状態でクリック → トグル
    input.addEventListener('click', () => {
      if (alreadyFocused) {
        if (!dd.hasAttribute('hidden')) {
          closeDD();
        } else {
          fetchCloudData();
          if (!input.value.trim()) renderHistory(); else openDD();
        }
      }
    });

    input.addEventListener('input', () => { updateClear(); onInput(); });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = '';
        currentQuery = '';
        updateClear();
        renderHistory();
        input.focus();
      });
    }

    /* ── DD クリック委任 ── */
    dd.addEventListener('click', e => {
      const item = e.target.closest('[data-action="pick"]');
      if (item) {
        const word = item.dataset.word || item.textContent.trim();
        input.value = word;
        closeDD();
        recordSearch(word);
        window.location.href = `${SEARCH_URL}?q=${encodeURIComponent(word)}&type=product`;
      }
    });

    /* ── フォーム送信 ── */
    if (form) {
      form.addEventListener('submit', e => {
        const q = input.value.trim();
        if (!q) { e.preventDefault(); return; }
        recordSearch(q);
      });
    }

    /* ── 外クリックで閉じる ── */
    document.addEventListener('click', e => {
      if (!wrap.contains(e.target)) closeDD();
    });
  }

  /* ── 起動 ── */
  function boot() {
    fetchCloudData(); // バックグラウンドで事前ロード
    document.querySelectorAll('.psb-wrap').forEach(initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
